import type { BufferGeometry, InterleavedBufferAttribute } from 'three'
import { getLoggerManager } from '@/shared/logging'

const log = getLoggerManager().getLogger('draco:decode')

const COMPONENT_TYPES: Readonly<Record<number, string>> = {
  5120: 'Int8Array',
  5121: 'Uint8Array',
  5122: 'Int16Array',
  5123: 'Uint16Array',
  5125: 'Uint32Array',
  5126: 'Float32Array',
}

export interface DracoDecodeServiceOptions {
  /** 宿主部署的 Three.js 匹配版本 decoder 目录；不会使用公共 CDN。 */
  readonly decoderPath: string
  /** 同时运行的解码任务与 Worker 上限，默认 2，合法范围 1..16。 */
  readonly workerLimit?: number
  /** Worker 不响应时的失败期限，默认 120 秒。 */
  readonly taskTimeoutMs?: number
}

export interface DracoAttributeExpectation {
  readonly count: number
  readonly itemSize: number
  readonly componentType: number
  readonly normalized: boolean
}

export interface DracoDecodeRequest {
  readonly data: ArrayBuffer | ArrayBufferView
  readonly attributeIDs: Readonly<Record<string, number>>
  readonly attributeTypes: Readonly<Record<string, string>>
  readonly attributes?: Readonly<Record<string, DracoAttributeExpectation>>
  readonly indexCount?: number
  readonly maxKnownBytes?: number
  readonly maxGeometryBytes?: number
  readonly signal?: AbortSignal
  readonly source?: string
}

export interface DracoDecodeResult {
  /** 调用方独占，必须负责最终 dispose。 */
  readonly geometry: BufferGeometry
  readonly inputBytes: number
  readonly outputBytes: number
  /** Worker/WASM 内部高水位无法从 Three addon 观测。 */
  readonly workerPeakBytes: null
}

export interface IDracoDecodeService {
  decode(request: DracoDecodeRequest): Promise<DracoDecodeResult>
  dispose(): void
}

export class DracoBudgetExceededError extends Error {
  readonly name = 'DracoBudgetExceededError'

  constructor(
    readonly kind: 'transient' | 'geometry',
    readonly actualBytes: number,
    readonly limitBytes: number
  ) {
    super(`Draco ${kind} 成本 ${actualBytes} 超过额度 ${limitBytes}`)
  }
}

interface DracoWorker extends Worker {
  _callbacks: Record<number, { reject(error: unknown): void }>
}

interface DracoLoaderInternals {
  _getWorker(taskID: number, taskCost: number): Promise<DracoWorker>
  workerPool: DracoWorker[]
  decodeGeometry(
    data: ArrayBuffer,
    config: {
      attributeIDs: Readonly<Record<string, number>>
      attributeTypes: Readonly<Record<string, string>>
      useUniqueIDs: boolean
    }
  ): Promise<BufferGeometry>
}

type DracoLoader = import('three/addons/loaders/DRACOLoader.js').DRACOLoader

interface Task {
  readonly request: DracoDecodeRequest
  readonly settle: (result?: DracoDecodeResult, error?: unknown) => void
  readonly abort: () => void
  active: boolean
  settled: boolean
  timer?: ReturnType<typeof setTimeout>
}

function validateLimit(value: number, name: string, maximum: number): number {
  if (!Number.isFinite(value)) throw new RangeError(`${name} 必须为有限数`)
  return Math.max(1, Math.min(maximum, Math.floor(value)))
}

function inputBytes(data: ArrayBuffer | ArrayBufferView): Uint8Array {
  return ArrayBuffer.isView(data)
    ? new Uint8Array(data.buffer, data.byteOffset, data.byteLength)
    : new Uint8Array(data)
}

function getOutputBytes(geometry: BufferGeometry): number {
  const arrays = new Set<ArrayBufferView>()
  const add = (attribute: InterleavedBufferAttribute | import('three').BufferAttribute) => {
    arrays.add('data' in attribute ? attribute.data.array : attribute.array)
  }
  for (const attribute of Object.values(geometry.attributes)) add(attribute)
  if (geometry.index) add(geometry.index)
  return [...arrays].reduce((bytes, array) => bytes + array.byteLength, 0)
}

function estimateKnownOutput(request: DracoDecodeRequest): number {
  let bytes = (request.indexCount ?? 0) * 4
  for (const attribute of Object.values(request.attributes ?? {})) {
    const type = COMPONENT_TYPES[attribute.componentType]
    const componentBytes =
      type === 'Float32Array' || type === 'Uint32Array'
        ? 4
        : type === 'Int16Array' || type === 'Uint16Array'
          ? 2
          : 1
    const stride = Math.ceil((attribute.itemSize * componentBytes) / 4) * 4
    bytes += attribute.count * stride
  }
  if (!Number.isSafeInteger(bytes)) throw new Error('Draco 已知输出成本溢出')
  return bytes
}

function validateGeometry(request: DracoDecodeRequest, geometry: BufferGeometry): number {
  const position = geometry.getAttribute('position')
  if (!position || !Number.isSafeInteger(position.count) || position.count < 3)
    throw new Error('Draco 输出缺少有效 POSITION')
  for (const [name, expected] of Object.entries(request.attributes ?? {})) {
    const attribute = geometry.getAttribute(name)
    const array = attribute && ('data' in attribute ? attribute.data.array : attribute.array)
    if (
      !attribute ||
      attribute.count !== expected.count ||
      attribute.itemSize !== expected.itemSize ||
      array?.constructor.name !== COMPONENT_TYPES[expected.componentType]
    )
      throw new Error(`Draco ${name} 输出与 accessor 不一致`)
    attribute.normalized = expected.normalized
  }
  const index = geometry.getIndex()
  if (index?.array.constructor.name !== 'Uint32Array')
    throw new Error('Draco 输出不是 Uint32 三角索引')
  if (
    index.count % 3 !== 0 ||
    (request.indexCount !== undefined && index.count !== request.indexCount)
  )
    throw new Error('Draco 三角索引数量与声明不一致')
  for (let i = 0; i < index.count; i++)
    if (index.getX(i) >= position.count) throw new Error('Draco 索引越出 POSITION 范围')
  return getOutputBytes(geometry)
}

class DracoDecodeService implements IDracoDecodeService {
  private readonly _workerLimit: number
  private readonly _taskTimeoutMs: number
  private readonly _queue: Task[] = []
  private readonly _tasks = new Set<Task>()
  private _active = 0
  private _loader?: DracoLoader
  private _initializing?: Promise<DracoLoader>
  private _failure?: Error
  private _disposed = false

  constructor(private readonly _options: DracoDecodeServiceOptions) {
    if (!_options.decoderPath?.trim()) throw new Error('Draco decoderPath 不能为空')
    this._workerLimit = validateLimit(_options.workerLimit ?? 2, 'Draco workerLimit', 16)
    this._taskTimeoutMs = validateLimit(
      _options.taskTimeoutMs ?? 120_000,
      'Draco taskTimeoutMs',
      3_600_000
    )
  }

  decode(request: DracoDecodeRequest): Promise<DracoDecodeResult> {
    if (this._disposed) return Promise.reject(new Error('Draco 服务已销毁'))
    if (this._failure) return Promise.reject(this._failure)
    if (request.signal?.aborted) return Promise.reject(new Error('Draco 解码已取消'))
    for (const [name, id] of Object.entries(request.attributeIDs))
      if (!Number.isSafeInteger(id) || id < 0 || !request.attributeTypes[name])
        return Promise.reject(new Error(`Draco 属性 ID 或类型无效: ${name}`))
    const geometryKnown = estimateKnownOutput(request)
    const known = inputBytes(request.data).byteLength * 2 + geometryKnown
    if (request.maxKnownBytes !== undefined && known > request.maxKnownBytes)
      return Promise.reject(new DracoBudgetExceededError('transient', known, request.maxKnownBytes))
    if (request.maxGeometryBytes !== undefined && geometryKnown > request.maxGeometryBytes)
      return Promise.reject(
        new DracoBudgetExceededError('geometry', geometryKnown, request.maxGeometryBytes)
      )
    return new Promise((resolve, reject) => {
      const task: Task = {
        request,
        active: false,
        settled: false,
        settle: (result, error) => {
          if (task.settled) {
            result?.geometry.dispose()
            return
          }
          task.settled = true
          if (error) reject(error)
          else resolve(result!)
        },
        abort: () => {
          if (!task.active) {
            task.settle(undefined, new Error('Draco 解码已取消'))
            const index = this._queue.indexOf(task)
            if (index >= 0) this._queue.splice(index, 1)
            this._tasks.delete(task)
          }
        },
      }
      this._tasks.add(task)
      request.signal?.addEventListener('abort', task.abort, { once: true })
      this._queue.push(task)
      this._drain()
    })
  }

  dispose(): void {
    if (this._disposed) return
    this._disposed = true
    this._rejectAll(new Error('Draco 服务已销毁'))
    this._rejectWorkers(new Error('Draco 服务已销毁'))
    this._loader?.dispose()
    this._loader = undefined
  }

  private _rejectAll(error: Error): void {
    for (const task of this._tasks) {
      task.settle(undefined, error)
      task.request.signal?.removeEventListener('abort', task.abort)
      if (task.timer) clearTimeout(task.timer)
    }
    this._tasks.clear()
    this._queue.length = 0
  }

  private _fail(error: Error): void {
    if (this._failure || this._disposed) return
    this._failure = error
    log.error(`解码服务失败: ${error.message}`)
    this._rejectAll(error)
    this._rejectWorkers(error)
    this._loader?.dispose()
    this._loader = undefined
  }

  private _rejectWorkers(error: Error): void {
    for (const worker of (this._loader as unknown as DracoLoaderInternals | undefined)
      ?.workerPool ?? [])
      for (const callback of Object.values(worker._callbacks ?? {})) callback.reject(error)
  }

  private _watchWorkers(loader: DracoLoader): void {
    const internal = loader as unknown as DracoLoaderInternals
    const getWorker = internal._getWorker.bind(loader)
    const watched = new WeakSet<DracoWorker>()
    internal._getWorker = async (taskID, cost) => {
      const worker = await getWorker(taskID, cost)
      if (!watched.has(worker)) {
        watched.add(worker)
        const fail = (kind: string) => {
          const error = new Error(`Draco Worker ${kind}`)
          for (const callback of Object.values(worker._callbacks)) callback.reject(error)
          this._fail(error)
        }
        worker.addEventListener('error', () => fail('error'))
        worker.addEventListener('messageerror', () => fail('messageerror'))
      }
      return worker
    }
  }

  private _getLoader(): Promise<DracoLoader> {
    if (this._initializing) return this._initializing
    this._initializing = import('three/addons/loaders/DRACOLoader.js')
      .then(({ DRACOLoader }) => {
        if (this._disposed || this._failure) throw this._failure ?? new Error('Draco 服务已销毁')
        const loader = new DRACOLoader()
        loader.setDecoderPath(this._options.decoderPath)
        loader.setWorkerLimit(this._workerLimit)
        this._watchWorkers(loader)
        this._loader = loader
        return loader
      })
      .catch((error: unknown) => {
        this._fail(error instanceof Error ? error : new Error(String(error)))
        throw error
      })
    return this._initializing
  }

  private _drain(): void {
    while (!this._disposed && !this._failure && this._active < this._workerLimit) {
      const task = this._queue.shift()
      if (!task) return
      if (task.settled) continue
      task.active = true
      this._active++
      void this._run(task)
    }
  }

  private async _run(task: Task): Promise<void> {
    try {
      task.timer = setTimeout(
        () => this._fail(new Error('Draco 解码初始化或 Worker 超时')),
        this._taskTimeoutMs
      )
      const loader = await this._getLoader()
      if (task.settled) return
      if (task.request.signal?.aborted) {
        task.settle(undefined, new Error('Draco 解码已取消'))
        return
      }
      const ownData = inputBytes(task.request.data).slice().buffer
      const compressedBytes = ownData.byteLength
      const geometry = await (loader as unknown as DracoLoaderInternals).decodeGeometry(ownData, {
        attributeIDs: task.request.attributeIDs,
        attributeTypes: task.request.attributeTypes,
        useUniqueIDs: true,
      })
      if (task.settled || this._disposed || task.request.signal?.aborted) {
        geometry.dispose()
        if (task.request.signal?.aborted) task.settle(undefined, new Error('Draco 解码已取消'))
        return
      }
      try {
        const outputBytes = validateGeometry(task.request, geometry)
        if (
          task.request.maxKnownBytes !== undefined &&
          compressedBytes * 2 + outputBytes > task.request.maxKnownBytes
        )
          throw new DracoBudgetExceededError(
            'transient',
            compressedBytes * 2 + outputBytes,
            task.request.maxKnownBytes
          )
        if (
          task.request.maxGeometryBytes !== undefined &&
          outputBytes > task.request.maxGeometryBytes
        )
          throw new DracoBudgetExceededError('geometry', outputBytes, task.request.maxGeometryBytes)
        task.settle({
          geometry,
          inputBytes: inputBytes(task.request.data).byteLength,
          outputBytes,
          workerPeakBytes: null,
        })
      } catch (error) {
        geometry.dispose()
        throw error
      }
    } catch (error) {
      task.settle(
        undefined,
        error instanceof DracoBudgetExceededError
          ? error
          : new Error(
              `Draco 解码失败 (${task.request.source ?? 'unknown'}): ${error instanceof Error ? error.message : String(error)}`
            )
      )
    } finally {
      if (task.timer) clearTimeout(task.timer)
      task.request.signal?.removeEventListener('abort', task.abort)
      this._tasks.delete(task)
      this._active--
      this._drain()
    }
  }
}

export function createDracoDecodeService(options: DracoDecodeServiceOptions): IDracoDecodeService {
  return new DracoDecodeService(options)
}
